import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Tooltip } from '~/components';
import { env } from '~/lib/env';

import type { AssetFamily } from '../../lib/types';
import { PerformanceDeltaLabel } from './PerformanceDeltaLabel';
import { ReleaseTag } from './ReleaseTag';
import { TimeRangeLabel } from './TimeRangeLabel';
import { TrackTableLinkMenu } from './TrackTableLinkMenu';
import { formatDateByLocale } from './utils';

type TimeRange = 'TW' | 'TM' | 'TY';

export function useTrackTableColumns({ range }: { range: TimeRange }) {
  const { t } = useTranslation('roster');

  const isAssetPerformanceEnabled =
    env('VITE_WMG_ASSET_PERFORMANCE_ENABLED') === 'true';
  const today = useMemo(() => new Date(), []);

  return useMemo(() => {
    const columnHelper = createColumnHelper<AssetFamily>();

    const formatMetric = (
      row: AssetFamily,
      metricNumber: number | undefined,
    ) => {
      const releaseDate = row.initialReleaseDate
        ? new Date(row.initialReleaseDate)
        : null;
      if (
        releaseDate &&
        releaseDate < today &&
        (!!metricNumber || metricNumber === 0) &&
        Number.isFinite(metricNumber)
      ) {
        return metricNumber.toLocaleString();
      } else {
        return null;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const trackTableColumns: ColumnDef<AssetFamily, any>[] = [
      columnHelper.accessor('title', {
        id: 'title',
        enableSorting: true,
        header: (header) => {
          const sortDirection = header.column.getIsSorted();
          let iconType = 'arrow-up-arrow-down';
          if (sortDirection) {
            iconType = sortDirection === 'asc' ? 'arrow-up' : 'arrow-down';
          }
          const iconVariant = sortDirection ? 'solid' : 'light';

          return (
            <div className="flex items-center gap-sm">
              <span>{t('trackList.header.trackTitle')}</span>
              <Tooltip
                content={
                  <span className="font-semibold">
                    {t('trackList.tooltips.sortBy', {
                      field: t('trackList.header.trackTitle'),
                    })}
                  </span>
                }
                delayDuration={0}
                sideOffset={16}
                side="top"
                align="end"
                size="md"
              >
                <Icon
                  type={iconType}
                  variant={iconVariant}
                  aria-hidden="true"
                  size="sm"
                  onClick={header.column.getToggleSortingHandler()}
                />
              </Tooltip>
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="flex items-center gap-sm py-md">
            <div className="font-bold">{row.original.title}</div>
            <ReleaseTag releaseDateString={row.original.initialReleaseDate} />
          </div>
        ),
      }),
      columnHelper.accessor('initialReleaseDate', {
        id: 'initialReleaseDate',
        enableSorting: true,
        header: (header) => {
          const sortDirection = header.column.getIsSorted();
          let iconType = 'arrow-up-arrow-down';
          if (sortDirection) {
            iconType = sortDirection === 'asc' ? 'arrow-up' : 'arrow-down';
          }
          const iconVariant = sortDirection ? 'solid' : 'light';

          return (
            <div className="flex items-center gap-sm">
              <span>{t('trackList.header.releaseDate')}</span>
              <Tooltip
                content={
                  <span className="font-semibold">
                    {t('trackList.tooltips.sortBy', {
                      field: t('trackList.header.releaseDate'),
                    })}
                  </span>
                }
                delayDuration={0}
                sideOffset={16}
                side="top"
                align="end"
                size="md"
              >
                <Icon
                  type={iconType}
                  variant={iconVariant}
                  aria-hidden="true"
                  size="sm"
                  onClick={header.column.getToggleSortingHandler()}
                />
              </Tooltip>
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="p-sm">
            {row.original.initialReleaseDate ? (
              formatDateByLocale(row.original.initialReleaseDate)
            ) : (
              <span className="px-sm py-md pl-xl text-subtle">--</span>
            )}
          </div>
        ),
      }),
    ];

    // Only create performance columns if feature is enabled
    if (isAssetPerformanceEnabled) {
      trackTableColumns.push(
        columnHelper.accessor('performance.streaming.aggregated.totalStreams', {
          id: 'streamsRTD',
          enableSorting: false,
          header: () => (
            <div className="flex items-end gap-xs text-nowrap">
              <span>{t('trackList.header.streams')}</span>
              <TimeRangeLabel timeRange="RTD" />
            </div>
          ),
          cell: ({ row }) => {
            const totalStreams = formatMetric(
              row.original,
              row.original.performance?.streaming?.aggregated?.totalStreams,
            );

            return totalStreams ? (
              <span className="p-sm">{totalStreams}</span>
            ) : (
              <span className="px-sm py-md pl-md text-subtle">--</span>
            );
          },
        }),

        columnHelper.accessor(
          'performance.streaming.aggregated.periodStreams.current',
          {
            id: 'streams',
            enableSorting: false,
            header: () => (
              <div className="flex items-end gap-xs text-nowrap">
                <span>{t('trackList.header.streams')}</span>
                <TimeRangeLabel timeRange={range} />
              </div>
            ),
            cell: ({ row }) => {
              const periodStreams = formatMetric(
                row.original,
                row.original.performance?.streaming?.aggregated?.periodStreams
                  ?.current,
              );
              return periodStreams ? (
                <div className="flex flex-col p-sm">
                  <span>{periodStreams}</span>
                  <PerformanceDeltaLabel
                    delta={
                      row.original.performance?.streaming?.aggregated
                        ?.periodStreams?.deltaRatio
                    }
                  />
                </div>
              ) : (
                <span className="px-sm py-md pl-md text-subtle">--</span>
              );
            },
          },
        ),

        columnHelper.accessor(
          'performance.streaming.aggregated.organicStreams.current',
          {
            id: 'organicStreams',
            enableSorting: false,
            header: () => (
              <div className="flex items-end gap-xs text-nowrap">
                <span>{t('trackList.header.organicStreams')}</span>
                <TimeRangeLabel timeRange={range} />
              </div>
            ),
            cell: ({ row }) => {
              const organicStreams = formatMetric(
                row.original,
                row.original.performance?.streaming?.aggregated?.organicStreams
                  ?.current,
              );
              return organicStreams ? (
                <div className="flex flex-col p-sm">
                  <span>{organicStreams}</span>
                  <PerformanceDeltaLabel
                    delta={
                      row.original.performance?.streaming?.aggregated
                        ?.organicStreams?.deltaRatio
                    }
                  />
                </div>
              ) : (
                <span className="px-sm py-md pl-md text-subtle">--</span>
              );
            },
          },
        ),
        columnHelper.accessor(
          'performance.engagement.aggregated.socialCreations.current',
          {
            id: 'socialCreations',
            enableSorting: false,
            header: () => (
              <div className="flex items-end gap-xs text-nowrap">
                <span>{t('trackList.header.socialCreations')}</span>
                <TimeRangeLabel timeRange="TW" />
              </div>
            ),
            cell: ({ row }) => {
              const socialCreations = formatMetric(
                row.original,
                row.original.performance?.engagement?.aggregated
                  ?.socialCreations?.current,
              );
              return socialCreations ? (
                <div className="flex flex-col p-sm">
                  <span>{socialCreations}</span>
                  <PerformanceDeltaLabel
                    delta={
                      row.original.performance?.engagement?.aggregated
                        ?.socialCreations?.deltaRatio
                    }
                  />
                </div>
              ) : (
                <span className="px-sm py-md pl-md text-subtle">--</span>
              );
            },
          },
        ),
        columnHelper.display({
          id: 'additionalMenu',
          enableSorting: false,
          header: () => null,
          cell: ({ row }) =>
            row.original.links?.opus ? (
              <TrackTableLinkMenu trackData={row.original} />
            ) : null,
        }),
      );
    }

    return trackTableColumns;
  }, [t, range, isAssetPerformanceEnabled, today]);
}
